import { Box, Stack, styled, SxProps, Typography, useTheme } from "@mui/material";
import MuiCard from "@mui/material/Card";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { usePanelsBySwitchgear } from "../data/Assets/usePanelsBySwitchgear";
import { compareTwoPanelNumbers } from "../hvSubstations/PanelNumberOrdering";
import {
  getAssetTypeLabel,
  managedSurveyAssetType,
  ManagedSurveyAssetType,
  UTP2_VARIABLE_CHAR,
} from "../shared/constants/managedSurveyAssetTypes";
import { MeasurementComponent, SubLocation } from "../shared/enums/Result";
import { MediaButton } from "../shared/FormLayout/IconButtons/MediaButton";
import { doesAssetHaveMedia } from "../shared/helpers/ResultHelpers";
import { getOverallRAGStatus } from "../shared/helpers/SubLocationHelpers";
import { HorizontalScrollContainer } from "../shared/HorizontalScrollContainer";
import { IdName } from "../shared/interfaces/Asset";
import { Measurement, MeasurementDetail, PanelMeasurement, Result } from "../shared/interfaces/Result";
import { getSubAssetType } from "../shared/interfaces/SurveyableAsset";
import { Panel } from "../shared/interfaces/Switchgear";
import { ComponentMeasurementIcons } from "./ComponentMeasurementIcons";
import { PartialResultPanelAssetUI } from "./PartialResultPanelAssetUI";
import { PartialResultReducer, Step } from "./PartialResultReducer";
import RAGStatusChip from "./RAGStatusChip";
import { SubLocationGrid } from "./SubLocation/SubLocationGrid";
import { UploadImageButton } from "./UploadImageButton";

interface Props {
  result?: Result;
  onIconClick: (measurement: MeasurementDetail<Measurement>, position: number | undefined, newTab: boolean) => void;
  onMediaClick: (selectedAsset?: IdName) => void;
  isPartialResult?: boolean;
  partialResultReducer?: PartialResultReducer;
  onImageUploadSuccess?: () => void;
  onImageUploadError?: (response: Response) => Promise<void>;
  assetType?: ManagedSurveyAssetType;
  panelMeasurements?: PanelMeasurement[];
  parentAssetId?: number;
  canAddResult?: boolean;
}

interface MeasurementComponentOption {
  type: keyof typeof MeasurementComponent;
  keySuffix: string;
  sublocation: SubLocation | null;
  component: React.ComponentType<any>;
}

export const measurementOptions: Record<ManagedSurveyAssetType, MeasurementComponentOption[]> = {
  $UTP2_DEFAULT: [
    {
      type: "Cables",
      keySuffix: "cables",
      sublocation: null,
      component: SubLocationGrid,
    },
    {
      type: "CableBox",
      keySuffix: "cableBox",
      sublocation: null,
      component: SubLocationGrid,
    },
    {
      type: "CTChamber",
      keySuffix: "CTChamber",
      sublocation: SubLocation.None,
      component: ComponentMeasurementIcons,
    },
    {
      type: "VTChamber",
      keySuffix: "VTChamber",
      sublocation: SubLocation.None,
      component: ComponentMeasurementIcons,
    },
    {
      type: "UpperBusbars",
      keySuffix: "UpperBusbars",
      sublocation: SubLocation.None,
      component: ComponentMeasurementIcons,
    },
    {
      type: "LowerBusbars",
      keySuffix: "LowerBusbars",
      sublocation: SubLocation.None,
      component: ComponentMeasurementIcons,
    },
    {
      type: "Spouts",
      keySuffix: "Spouts",
      sublocation: null,
      component: SubLocationGrid,
    },
    {
      type: "CircuitBreaker",
      keySuffix: "CircuitBreaker",
      sublocation: SubLocation.None,
      component: ComponentMeasurementIcons,
    },
  ],
  $TRANSFORMER: [
    {
      type: "PrimaryCables",
      keySuffix: "PrimaryCables",
      sublocation: null,
      component: SubLocationGrid,
    },
    {
      type: "PrimaryCableBox",
      keySuffix: "PrimaryCableBox",
      sublocation: SubLocation.None,
      component: ComponentMeasurementIcons,
    },
    {
      type: "PrimaryBushing",
      keySuffix: "PrimaryBushing",
      sublocation: null,
      component: SubLocationGrid,
    },
    {
      type: "MainTank",
      keySuffix: "MainTank",
      sublocation: SubLocation.None,
      component: ComponentMeasurementIcons,
    },
    {
      type: "TapChanger",
      keySuffix: "TapChanger",
      sublocation: SubLocation.None,
      component: ComponentMeasurementIcons,
    },
    {
      type: "SecondaryBushing1",
      keySuffix: "SecondaryBushing1",
      sublocation: null,
      component: SubLocationGrid,
    },
    {
      type: "SecondaryCables1",
      keySuffix: "SecondaryCables1",
      sublocation: null,
      component: SubLocationGrid,
    },
    {
      type: "SecondaryCableBox1",
      keySuffix: "SecondaryCableBox1",
      sublocation: SubLocation.None,
      component: ComponentMeasurementIcons,
    },
    {
      type: "SecondaryBushing2",
      keySuffix: "SecondaryBushing2",
      sublocation: null,
      component: SubLocationGrid,
    },
    {
      type: "SecondaryCableBox2",
      keySuffix: "SecondaryCableBox2",
      sublocation: SubLocation.None,
      component: ComponentMeasurementIcons,
    },
    {
      type: "SecondaryCables2",
      keySuffix: "SecondaryCables2",
      sublocation: null,
      component: SubLocationGrid,
    },
  ],
  $GIS_SWITCHGEAR: [
    {
      type: "Busbar1",
      keySuffix: "Busbar1",
      sublocation: SubLocation.None,
      component: ComponentMeasurementIcons,
    },
    {
      type: "Busbar2",
      keySuffix: "Busbar2",
      sublocation: SubLocation.None,
      component: ComponentMeasurementIcons,
    },
    {
      type: "DisconnectorSwitch1",
      keySuffix: "DisconnectorSwitch1",
      sublocation: SubLocation.None,
      component: ComponentMeasurementIcons,
    },
    {
      type: "DisconnectorSwitch2",
      keySuffix: "DisconnectorSwitch2",
      sublocation: SubLocation.None,
      component: ComponentMeasurementIcons,
    },
    {
      type: "CurrentTransformer1",
      keySuffix: "CurrentTransformer1",
      sublocation: SubLocation.None,
      component: ComponentMeasurementIcons,
    },
    {
      type: "CurrentTransformer2",
      keySuffix: "CurrentTransformer2",
      sublocation: SubLocation.None,
      component: ComponentMeasurementIcons,
    },
    {
      type: "CircuitBreaker",
      keySuffix: "CircuitBreaker",
      sublocation: SubLocation.None,
      component: ComponentMeasurementIcons,
    },
    {
      type: "VoltageTransformer",
      keySuffix: "VoltageTransformer",
      sublocation: SubLocation.None,
      component: ComponentMeasurementIcons,
    },
    {
      type: "GasInsulatedLine",
      keySuffix: "GasInsulatedLine",
      sublocation: SubLocation.None,
      component: ComponentMeasurementIcons,
    },
    {
      type: "LightningArrester",
      keySuffix: "LightningArrester",
      sublocation: SubLocation.None,
      component: ComponentMeasurementIcons,
    },
    {
      type: "EarthingSwitch",
      keySuffix: "EarthingSwitch",
      sublocation: SubLocation.None,
      component: ComponentMeasurementIcons,
    },
  ],
  $UTP2_SIMPLE: [
    {
      type: "RearLower",
      keySuffix: "RearLower",
      sublocation: null,
      component: SubLocationGrid,
    },
    {
      type: "RearMiddle",
      keySuffix: "RearMiddle",
      sublocation: null,
      component: SubLocationGrid,
    },
    {
      type: "RearUpper",
      keySuffix: "RearUpper",
      sublocation: null,
      component: SubLocationGrid,
    },
    {
      type: "FrontMiddle",
      keySuffix: "FrontMiddle",
      sublocation: null,
      component: SubLocationGrid,
    },
    {
      type: "FrontLower",
      keySuffix: "FrontLower",
      sublocation: null,
      component: SubLocationGrid,
    },
  ],
  $OUTDOOR_SWITCHYARD: [
    {
      type: "IncomerCable",
      keySuffix: "IncomerCable",
      sublocation: null,
      component: SubLocationGrid,
    },
    {
      type: "OverheadSwitch1",
      keySuffix: "OverheadSwitch1",
      sublocation: null,
      component: SubLocationGrid,
    },
    {
      type: "CircuitBreaker",
      keySuffix: "CircuitBreaker",
      sublocation: null,
      component: SubLocationGrid,
    },
    {
      type: "VoltageTransformer",
      keySuffix: "VoltageTransformer",
      sublocation: null,
      component: SubLocationGrid,
    },
    {
      type: "CurrentTransformer",
      keySuffix: "CurrentTransformer",
      sublocation: null,
      component: SubLocationGrid,
    },
    {
      type: "BusbarSection1",
      keySuffix: "BusbarSection1",
      sublocation: null,
      component: SubLocationGrid,
    },
    {
      type: "LightningArrester",
      keySuffix: "LightningArrester",
      sublocation: null,
      component: SubLocationGrid,
    },
    {
      type: "OverheadSwitch2",
      keySuffix: "OverheadSwitch2",
      sublocation: null,
      component: SubLocationGrid,
    },
    {
      type: "BusbarSection2",
      keySuffix: "BusbarSection2",
      sublocation: null,
      component: SubLocationGrid,
    },
  ],
  $UNKNOWN: [],
};

export function getFormattedAssetType(assetType?: string): ManagedSurveyAssetType {
  if (assetType == null) return managedSurveyAssetType.Switchgear;
  return assetType.startsWith(UTP2_VARIABLE_CHAR)
    ? (assetType as ManagedSurveyAssetType)
    : getAssetTypeLabel(assetType);
}

const Row = styled(Box)(() => ({
  display: "table-row",
  height: "100%",
}));

const Cell = styled(Box)(() => {
  return {
    display: "table-cell",
    verticalAlign: "middle",
    padding: "5px",
    height: "100%",
    maxWidth: "150px",
  };
});
const CardContainer = styled(Box)(() => {
  return {
    height: "100%",
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  };
});

const CARD_SX: SxProps = {
  width: "150px",
};

export function PanelMeasurementsOverview(props: Props) {
  const theme = useTheme();
  const { t } = useTranslation();
  const sortedPanelMeasurements = useMemo(() => {
    if (props.panelMeasurements)
      return [...props.panelMeasurements].sort((a, b) => compareTwoPanelNumbers(a.PanelNo, b.PanelNo, "asc"));
    else return [];
  }, [props.panelMeasurements]);

  const panelAssetsBySwitchgear = usePanelsBySwitchgear(
    props.partialResultReducer?.state.selectedSwitchgear?.id ?? props.parentAssetId,
    { onSuccess: onFetchPanelsSuccess },
  );

  const cableNaming = props.result?.dataValue?.MetaData?.SubstationGroup?.CableNamingScheme;

  const handlePanelUpdate = useCallback(() => {
    panelAssetsBySwitchgear.refetch();
  }, [panelAssetsBySwitchgear]);

  function onFetchPanelsSuccess(data: Panel[] | undefined) {
    if (!props.isPartialResult) return;

    if ((props.result?.dataValue.Assets?.length ?? 0) > 0) {
      props.partialResultReducer?.updateGroupPanelAssetIds(data, props.parentAssetId);
    } else {
      props.partialResultReducer?.updatePanelAssetIds(data);
    }
  }

  function getPartialResultPanelExcludedStatus(panelNo: string) {
    const partialResultPanel = props.partialResultReducer?.getPartialResultPanel(panelNo);
    return partialResultPanel?.isExcluded;
  }

  function handleImageUploadSuccess() {
    panelAssetsBySwitchgear.refetch();
    props.onImageUploadSuccess?.();
  }

  async function handleImageUploadError(response: Response) {
    await props.onImageUploadError?.(response);
  }

  const renderMeasurementRows = (components: MeasurementComponentOption[]) => {
    return components.map((component) => (
      <MeasurementRow key={component.type} title={t(`MeasurementComponents.${MeasurementComponent[component.type]}`)}>
        {sortedPanelMeasurements.map((panelMeasurement) => (
          <component.component
            key={`${panelMeasurement.PanelNo}-${component.keySuffix}`}
            panelMeasurement={panelMeasurement}
            componentType={MeasurementComponent[component.type]}
            sublocation={component.sublocation || SubLocation.None}
            cableNaming={cableNaming}
            onIconClick={props.onIconClick}
            assetType={getFormattedAssetType(props.assetType)}
          />
        ))}
      </MeasurementRow>
    ));
  };

  if (props.result)
    return (
      <React.Fragment>
        <HorizontalScrollContainer>
          <Box display="table" margin={2} width="100%" height="100%">
            {props.isPartialResult && props.partialResultReducer?.state.activeStep.step === Step.SelectPanels && (
              <Row>
                <Cell>
                  <Typography align="left" noWrap variant="subtitle2" color="textPrimary">
                    {t("panels.panelAsset", { val: getSubAssetType(props.assetType) })}
                  </Typography>
                </Cell>
                {sortedPanelMeasurements.map((panelMeasurement) => (
                  <Cell key={`partialResultPanelCell-${panelMeasurement.PanelNo}`}>
                    <PartialResultPanelAssetUI
                      panelAssets={panelAssetsBySwitchgear.data}
                      resultPanelNo={panelMeasurement.PanelNo}
                      resultPanelName={panelMeasurement.PanelName}
                      partialResultReducer={props.partialResultReducer}
                      onPanelUpdate={handlePanelUpdate}
                      sx={{ height: "100%", width: "100%" }}
                      cardProps={{ sx: CARD_SX }}
                      parentAssetId={props.parentAssetId}
                      parentAssetType={props.assetType}
                    />
                  </Cell>
                ))}
              </Row>
            )}
            <MeasurementRow title={t("panels.panelNumber", { val: getSubAssetType(props.assetType) })}>
              {sortedPanelMeasurements.map((panelMeasurement) => {
                const isExcluded = getPartialResultPanelExcludedStatus(panelMeasurement.PanelNo);
                return (
                  <Typography
                    key={`${panelMeasurement.PanelNo}-panelNumber`}
                    align="center"
                    className="p-2"
                    color={isExcluded ? theme.palette.text.disabled : "default"}
                  >
                    {panelMeasurement.PanelNo}
                  </Typography>
                );
              })}
            </MeasurementRow>
            <MeasurementRow title={t("panels.panelName", { val: getSubAssetType(props.assetType) })}>
              {sortedPanelMeasurements.map((panelMeasurement) => {
                const panelStepIndex = 3;
                const isExcluded = getPartialResultPanelExcludedStatus(panelMeasurement.PanelNo);
                const panelName =
                  !props.isPartialResult || (props.partialResultReducer?.state.activeStep.index ?? 0 >= panelStepIndex)
                    ? (panelAssetsBySwitchgear.data?.find((panel) => panel.panelNo === panelMeasurement.PanelNo)
                        ?.name ?? panelMeasurement.PanelName)
                    : panelMeasurement.PanelName;
                return (
                  <Typography
                    key={`${panelMeasurement.PanelNo}-panelName`}
                    align="center"
                    className="p-2"
                    color={isExcluded ? theme.palette.text.disabled : "default"}
                  >
                    {panelName}
                  </Typography>
                );
              })}
            </MeasurementRow>
            <MeasurementRow title={t("common.status")}>
              {sortedPanelMeasurements.map((panelMeasurement) => {
                const ragStatus = getOverallRAGStatus(panelMeasurement);

                return (
                  <Typography key={`${panelMeasurement.PanelNo}-panelStatus`} align="center" className="p-2">
                    <RAGStatusChip ragStatus={ragStatus} />
                  </Typography>
                );
              })}
            </MeasurementRow>

            <MeasurementRow title={t("common.media")}>
              {sortedPanelMeasurements.map((panelMeasurement: PanelMeasurement, index: number) => {
                const hasMedia = props.result && doesAssetHaveMedia(props.result, panelMeasurement.Panel360AssetId);
                return (
                  <Stack key={index} direction={"row"} spacing={1}>
                    <MediaButton
                      key={`${panelMeasurement.PanelNo}-mediaButton`}
                      disabled={!hasMedia}
                      tooltip={
                        hasMedia
                          ? `${t("common.viewMediaFor", { location: panelMeasurement.PanelName })}`
                          : `${t("common.noMediaFor", { location: panelMeasurement.PanelName })}`
                      }
                      onClick={() =>
                        props.onMediaClick({
                          id: panelMeasurement.Panel360AssetId,
                          name: panelMeasurement.PanelName,
                        })
                      }
                    />
                    {props.result && panelMeasurement.Panel360AssetId != null && props.canAddResult && (
                      <UploadImageButton
                        result={props.result}
                        captureId={Number(props.result.captureId)}
                        panelAssetId={panelMeasurement.Panel360AssetId}
                        surveyAssetName={panelMeasurement.PanelName}
                        onUploadSuccess={handleImageUploadSuccess}
                        onUploadError={handleImageUploadError}
                      />
                    )}
                  </Stack>
                );
              })}
            </MeasurementRow>
            {renderMeasurementRows(
              getFormattedAssetType(props.assetType)
                ? measurementOptions[getFormattedAssetType(props.assetType)]
                : measurementOptions.$UTP2_DEFAULT,
            )}
          </Box>
        </HorizontalScrollContainer>
      </React.Fragment>
    );
  else return <Box sx={{ height: "100vh" }}></Box>;
}

interface MeasurementRowProps {
  title: string;
  children: React.ReactNode[];
}

function MeasurementRow(props: MeasurementRowProps) {
  return (
    <Row>
      <Cell sx={{ width: 150 }}>
        <Typography align="left" noWrap variant="subtitle2" color="textPrimary" sx={{ width: 150 }}>
          {props.title}
        </Typography>
      </Cell>
      {React.Children.map(props.children, (child) => {
        return (
          <Cell>
            <CardContainer>
              <MuiCard
                variant="outlined"
                sx={{
                  ...CARD_SX,
                  alignItems: "center",
                  height: "100%",
                  minHeight: "3em",
                  padding: "5px",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                {child}
              </MuiCard>
            </CardContainer>
          </Cell>
        );
      })}
    </Row>
  );
}
